//Imports
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//Controls
import {
    Button,
    Form,
    Input,
    Modal,
    TextArea,
    Select
} from "@gpstrackit/gtc-react-controls";
import { NotificationManager, NotificationContainer } from "Components/Notifications";
import InputSearchMap from 'Components/Map/components/InputSearchMap';

//Actions
import { setFieldIdCustomerSelector } from 'Redux/actions'
import {
    workOrderGetLocation,
    reset,
    workOrderCreateLocation,
    workOrderEditLocation,
} from "./redux/slice";

//Variables
const INIT_DATA = {
    locationId: null,
    name: null,
    address: [],
    contact: null,
};

//Styles 
import './index.scss'

const CreateEditLocation = ({ refresh }) => {

    //States
    const [formData, setFormData] = useState(INIT_DATA);
    const [changes, setChanges] = useState({
        ...INIT_DATA,
        address: false
    })
    const [errorAddress, setErrorAddress] = useState(null);

    const { messages } = useIntl();

    let typeList = [
        { id: "regular", name: messages['workOrder_location_regular'] },
        { id: "billing", name: messages['workOrder_location_billing'] }
    ]

    //Form refs
    const createEditLocationFormRef = Form.useForm();

    //Redux States
    const {
        loading,
        open,
        create,
        update,
        locationId,
        customerId,
        location,
        error
    } = useSelector((state) => state.workOrdersCreateEditLocationRedux);

    const dispatch = useDispatch();

    //Other hooks
    const title = useMemo(() => {
        return location || locationId ? `${messages['workOrder_location_edit_title']} ${location ? `"${location?.name}"` : ''}` : messages['workOrder_location_create_title']
    }, [location, locationId]);

    //Handlers and utils
    const handleClose = () => {
        setErrorAddress(null)
        setFormData(INIT_DATA);
        dispatch(reset());
    };

    const handleSuccess = () => {
        NotificationManager.success(
            messages['workOrder_location_location_saved'],
            messages["success"],
            3000,
            null,
            null,
            "success"
        );
        refresh && refresh();
        handleClose();
    };

    const handleCreateEditContact = () => {
        const address = formData?.address?.[0] || {};
        const isEditing = !!locationId;

        let payload = {};

        if (isEditing) {
            if (changes.name || changes.type | changes.address || changes.contact) {
                payload = {
                    id: locationId,
                    ...(changes.name && { name: formData?.name }),
                    ...(changes.address && address.fulladdress && {
                        address: {
                            address: address.fulladdress,
                            city: address.city,
                            formatted: address.fulladdress,
                            state: address.state,
                            zip: address.postalcode,
                        },
                        latitude: address.lat,
                        longitude: address.lng,
                    }),
                    ...(changes.type && { locationType: formData?.type?.id }),
                    ...(changes.contact && { contact: formData?.contact }),
                };
            } else {
                NotificationManager.info(
                    messages['workOrder_location_no_changes_to_save'],
                    messages["info"],
                    3000,
                    null,
                    null,
                    "success"
                );
                return
            }
        } else {
            payload = {
                name: formData?.name,
                locationType: formData?.type?.id,
                address: address.fulladdress
                    ? {
                        address: address.fulladdress,
                        city: address.city,
                        formatted: address.fulladdress,
                        state: address.state,
                        zip: address.postalcode,
                    }
                    : undefined,
                latitude: address.lat || undefined,
                longitude: address.lng || undefined,
                contact: formData?.contact,
                customerId: customerId
            };
        }

        if (!isEditing) {
            dispatch(workOrderCreateLocation(payload));
        } else {
            dispatch(workOrderEditLocation(payload));
        }
    }

    useEffect(() => {
        if (open && locationId) {
            dispatch(workOrderGetLocation({ id: locationId }))
        }
    }, [open])

    useEffect(() => {
        if (create || update) {
            if (create?.id) {
                dispatch(setFieldIdCustomerSelector({ value: create?.id, field: 'locationId' }))
            }
            handleSuccess()
        }
    }, [create, update])

    useEffect(() => {
        if (location) {

            let currentLocation = JSON.parse(location?.address)
            let currentType = typeList.find((item) => item.id === location?.locationType)

            let locationInfo = {
                fulladdress: currentLocation?.formatted,
                address1: currentLocation?.address,
                city: currentLocation?.city,
                state: currentLocation?.state,
                postalcode: currentLocation?.zip,
                lat: location?.latitude,
                lng: location?.longitude,
                radius: 0,
                index: 0
            }

            let currentData = {
                name: location?.name,
                type: currentType,
                address: [locationInfo],
                contact: location?.contact
            }

            setFormData({
                ...formData,
                ...currentData
            })
            setTimeout(() => {
                createEditLocationFormRef.setValues({
                    ...currentData
                })
            }, 500)
        }
    }, [location])

    useEffect(() => {
        if (error) {
            let errorMessage = "";
            const errorLower = error?.error?.toLowerCase();

            const errorMessages = {
                "duplicated name": messages['workOrder_location_nameIsAlreadyInUse'],
            };

            for (const key in errorMessages) {
                if (errorLower?.includes(key)) {
                    errorMessage = errorMessages[key];
                    break;
                }
            }

            NotificationManager.error(
                errorMessage || messages['errorMessage'],
                messages["error"],
                3000,
            );
            dispatch(reset({ error: null }));
        }
    }, [error])

    return (
        <Modal
            title={title && title.length > 48 ? title.substring(0, 45) + '..."' : title}
            className="modal-create-entity-location"
            open={open}
            handleClose={handleClose}
            position="right"
            style={{ zIndex: "auto" }}
            size="small"
            loading={loading}
            footer={
                <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <Button
                        isDisabled={loading}
                        style={{ marginRight: 8 }}
                        onClick={handleClose}
                        variant="secondary"
                    >
                        {messages["cancel"]}
                    </Button>
                    <Button
                        isDisabled={loading}
                        onClick={() => {
                            createEditLocationFormRef?.submit()
                        }}
                        variant="primary"
                    >
                        {messages["save"]}
                    </Button>
                </div>
            }
        >
            <div style={{ padding: 24 }}>
                <Form
                    ref={createEditLocationFormRef.ref}
                    loading={loading}
                    onSubmit={() => {
                        const address = formData?.address?.[0] || {};
                        if (!formData.address.length || (address && !address?.state) || !formData?.name) {
                            if (!formData.address.length) {
                                setErrorAddress('required_field')
                            } else if (address && !address?.state) {
                                setErrorAddress('workOrder_location_addressFormatError')
                            }
                        } else {
                            setErrorAddress(null)
                            handleCreateEditContact()
                        }
                    }}
                /* onSubmit={(_values) => {
                    if (!formData.address.length) {
                        setErrorAddress('required_field')
                    }
                }} */
                >
                    <Form.Item
                        name="name"
                        label={messages['workOrder_location_name']}
                        rules={[{ required: true, message: 'required_field' }]}
                    >
                        <Input
                            id="name"
                            type="text"
                            placeholder={messages['workOrder_location_name']}
                            onChange={(value) => {
                                setFormData({ ...formData, name: value })
                                setChanges({ ...changes, name: true })
                            }}
                            maxLength={50}
                        />
                    </Form.Item>
                    {
                        location?.locationType !== "main" && (
                            <Form.Item
                                name="type"
                                label={messages['workOrder_location_type']}
                                rules={[{ required: true, message: 'required_field' }]}
                            >
                                <Select
                                    id="type"
                                    options={typeList || []}
                                    placeholder={messages['workOrder_location_type']}
                                    valueField="id"
                                    textField="name"
                                    style={{ width: "100%", fontFamily: '"Outfit", sans-serif' }}
                                    isSearchable
                                    onChange={(value) => {
                                        setFormData({ ...formData, type: value })
                                        setChanges({ ...changes, type: true })
                                    }}
                                    isClearable
                                    noOptionsMessage={"All"}
                                />
                            </Form.Item>
                        )
                    }
                    <div className={errorAddress ? "location-container-address-input-error" : ""} style={{ marginBottom: 15 }}>
                        <div style={{ marginBottom: 5 }}>
                            <span className="required-gtc-rc">*</span>
                            <label className="label-gtc-rc">{messages['workOrder_location_address']}</label>
                        </div>
                        {
                            !loading && (
                                <>
                                    <InputSearchMap
                                        normalView
                                        noMap
                                        onGetAddress={(value) => {
                                            setErrorAddress(null)
                                            setFormData({ ...formData, address: value })
                                            setChanges({ ...changes, address: true })
                                        }}
                                        onClear={() => {
                                            setFormData({ ...formData, address: [] })
                                            setChanges({ ...changes, address: true })
                                        }}
                                        onClearSetAddress={() => {
                                        }}
                                        onClearInput={() => {
                                        }}
                                        draggable={false}
                                        noOptionsText={messages['noOptions']}
                                        placeholder={messages['search']}
                                        addressValue={formData?.address[0]?.fulladdress}
                                    />
                                    {
                                        errorAddress &&
                                        <label className="assistive-gtc-rc-text assistive-gtc-rc-text-error" style={{ marginTop: 5 }}>{messages[errorAddress]}</label>
                                    }
                                </>
                            )
                        }
                    </div>
                    <Form.Item
                        name="contact"
                        label={messages['workOrder_location_descripcion']}
                    >
                        <TextArea
                            id="contact"
                            maxLength={500}
                            onChange={(value) => {
                                setFormData({ ...formData, contact: value })
                                setChanges({ ...changes, contact: true })
                            }}
                        />
                    </Form.Item>
                </Form>
            </div>
            <NotificationContainer />
        </Modal >
    );
};

export default CreateEditLocation;